import { AnchorWallet, useWallet } from '@solana/wallet-adapter-react'
import React, { createContext, useCallback, useEffect, useState } from 'react'
import firebase from 'firebase/compat/app'

import { useSignIn } from '../atoms/Signin'
import { auth } from '../data/FirebaseInit'
import { Provider } from '@project-serum/anchor'
import { solanaConnection } from '../data/Connection'
import { Profile } from '../interfaces'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { useToasts } from '@geist-ui/react'

export type TokenContextType = {
  user: firebase.User | undefined
  loading: boolean | undefined
  token: string | undefined
  isAuthenticated: boolean
  isConnected: boolean
  signIn: () => void
  disconnect: () => void
  provider: Provider | null
  profile: Profile | null
  fetchBalance: () => Promise<number>
}

const TokenContext = createContext<TokenContextType | null>(null)

export const TokenProvider = ({
  children,
}: {
  children: React.ReactElement | React.ReactElement[]
}): React.ReactElement => {
  const wallet = useWallet()
  const [loading, setLoading] = useState<boolean>(true)
  const [user, setUser] = useState<firebase.User | undefined>()
  const [token, setToken] = useState<string>()
  const isAuthenticated = !!(wallet.connected && token)
  const [, setToast] = useToasts()
  const { openSignInModal } = useSignIn()
  const [provider, setProvider] = useState<Provider | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const { visible } = useWalletModal()

  /** Disconnects from the wallet. */
  const disconnect = useCallback(async (): Promise<void> => {
    if (wallet) {
      await wallet.disconnect()
      await auth.signOut()

      setProvider(null)
      setProfile(null)
    }
  }, [wallet])

  const fetchBalance = useCallback(async (): Promise<number> => {
    if (wallet && wallet.publicKey) {
      const balance = await solanaConnection.getBalance(wallet.publicKey)
      return balance
    }
    return 0
  }, [wallet])

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUser(user)
        const token = await user.getIdToken()
        setToken(token)
      } else {
        setUser(undefined)
        setToken(undefined)
      }

      setLoading(false)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    if (wallet.connected && !visible) {
      const anchorWallet = {
        publicKey: wallet.publicKey,
        signTransaction: wallet.signTransaction,
        signAllTransactions: wallet.signAllTransactions,
      } as AnchorWallet

      setProvider(
        new Provider(solanaConnection, anchorWallet, {
          skipPreflight: true,
          commitment: 'processed',
          maxRetries: 10,
        })
      )

      if (!loading && !token) {
        openSignInModal()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.connected, visible, loading, token])

  useEffect(() => {
    ;(async (): Promise<void> => {
      if (!wallet?.publicKey) {
        return
      }

      const address = wallet.publicKey.toString()
      const balance = await solanaConnection.getBalance(wallet.publicKey)
      if (auth.currentUser?.uid && address !== auth.currentUser?.uid) {
        await auth.signOut()
      }
      setProfile({
        address: address,
        balance: (balance / 1000000000).toString(),
      })
    })()
  }, [wallet?.publicKey])

  useEffect(() => {
    if (isAuthenticated) {
      setToast({
        text: `Your wallet is connected.`,
        type: 'success',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  return (
    <TokenContext.Provider
      value={{
        user,
        loading,
        token,
        isAuthenticated,
        isConnected: wallet.connected,
        signIn: openSignInModal,
        disconnect,
        provider,
        profile,
        fetchBalance,
      }}
    >
      {children}
    </TokenContext.Provider>
  )
}

export default TokenContext
